import {
  ShareBoard,
  SortablePiece,
  shareColumns,
} from '@/meeting/meetingSummaryShareBoard'
import { BundledRelease, ReleaseColumn } from '@rolebase/shared/model/release'

// #77 — the console's « Nouveautés » page: the tickets of a release, sorted on
// the board of the compte rendu sharing (#115). This file turns database rows
// into that board and back, and decides what an import adds.

// A card as the console reads it (fragment AdminReleaseItem)
export interface ReleaseItemRow {
  id: string
  releaseVersion: string
  ticket?: number | null
  ticketTitle?: string | null
  proposedText: string
  sortColumn: string
  editedText?: string | null
  position: number
}

export interface ReleaseRow {
  version: string
  items: ReleaseItemRow[]
}

// ---------------------------------------------------------------------------
// Import

export interface ReleaseImport {
  releases: { version: string; title: string; publishedAt: string | null }[]
  items: {
    releaseVersion: string
    ticket: number | null
    ticketTitle: string | null
    proposedText: string
    sortColumn: ReleaseColumn
    position: number
  }[]
}

// What the files of the backend bring that the database does not have yet.
// Nothing already there is touched: Yohan's sorting survives any re-import.
// - A version absent from the database comes whole, lines without ticket
//   included, with the title and date of its file.
// - A version already there only gains its new tickets, at the end of « To
//   sort ». Lines without ticket are not re-added (nothing identifies them).
export function importFromBundled(
  bundled: BundledRelease[],
  existing: ReleaseRow[]
): ReleaseImport {
  const result: ReleaseImport = { releases: [], items: [] }
  const byVersion = new Map(existing.map((r) => [r.version, r]))

  for (const release of bundled) {
    const current = byVersion.get(release.version)
    if (!current) {
      result.releases.push({
        version: release.version,
        title: release.title,
        publishedAt: release.publishedAt,
      })
      release.items.forEach((item, index) =>
        result.items.push({
          releaseVersion: release.version,
          ...item,
          position: index,
        })
      )
      continue
    }

    const known = new Set(current.items.map((item) => item.ticket))
    let position =
      Math.max(-1, ...current.items.map((item) => item.position)) + 1
    for (const item of release.items) {
      if (item.ticket === null || known.has(item.ticket)) continue
      result.items.push({
        releaseVersion: release.version,
        ...item,
        sortColumn: 'todo',
        position: position++,
      })
    }
  }
  return result
}

// ---------------------------------------------------------------------------
// Board

// The text a card carries into the board: the one members would read
export function itemPieces(items: ReleaseItemRow[]): SortablePiece[] {
  return items.map((item) => ({ id: item.id, body: item.proposedText }))
}

export function boardFromItems(items: ReleaseItemRow[]): ShareBoard {
  const sorted = [...items].sort((a, b) => a.position - b.position)
  const columns = Object.fromEntries(
    shareColumns.map((column) => [
      column,
      sorted.filter((item) => item.sortColumn === column).map((i) => i.id),
    ])
  ) as ShareBoard['columns']
  const edited: ShareBoard['edited'] = {}
  for (const item of items) {
    if (item.editedText != null) edited[item.id] = item.editedText
  }
  return { columns, edited }
}

export interface ReleaseItemUpdate {
  id: string
  sortColumn: ReleaseColumn
  position: number
  editedText: string | null
}

// The cards whose column, position or rewritten text changed: only those are
// written back. Positions are counted across the whole board, column after
// column, so the order of the kept cards is the order members will read.
export function itemUpdates(
  board: ShareBoard,
  items: ReleaseItemRow[]
): ReleaseItemUpdate[] {
  const byId = new Map(items.map((item) => [item.id, item]))
  const updates: ReleaseItemUpdate[] = []
  let position = 0
  for (const column of shareColumns) {
    for (const id of board.columns[column]) {
      const item = byId.get(id)
      const editedText = board.edited[id] ?? null
      if (
        item &&
        (item.sortColumn !== column ||
          item.position !== position ||
          (item.editedText ?? null) !== editedText)
      ) {
        updates.push({ id, sortColumn: column, position, editedText })
      }
      position++
    }
  }
  return updates
}

// Publishing needs every card sorted and a title (the banner shows it)
export function canPublish(board: ShareBoard, title: string): boolean {
  return board.columns.todo.length === 0 && title.trim() !== ''
}
